// Page.js
import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import api from 'services/api';
import Breadcrumbs from 'components/common/Breadcrumbs';
import BannerWithSocialIcons from 'components/common/BannerWithSocialIcons';
import Summary from 'components/common/Summary';
import FileList from 'components/common/FileList';
import Badge from 'components/common/Badge';
import Button from 'components/common/Button';
import ShareButtons from 'components/common/ShareButtons';
import GalleryDisplay from 'components/common/GalleryDisplay';
import sharedStyles from 'styles/modules/ContentPage.module.css';
import MapCta from 'components/common/MapCta';
import LiensPratiques from 'components/common/LiensPratiques';
import styles from './Page.module.css';
import filestyle from 'components/common/FileList.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilePdf,
  faFileImage,
  faFileAlt,
  faExternalLinkAlt,
  faLanguage
} from '@fortawesome/free-solid-svg-icons';
import { ShareSocialOutline } from 'react-ionicons';
import CardBlock from 'components/pages/CardBlock'; // Importer le composant CardBlock
import PictoAccess from 'components/common/PictoAccess';
import { Helmet } from 'react-helmet';
import useBreakpoint from 'hooks/useBreakpoint'; // Responsive Hook
import Spinner from 'components/common/Spinner';
// Importez les images de drapeaux
import britishFlag from 'assets/images/flags/gb.svg';
import frenchFlag from 'assets/images/flags/fr.svg';

const Page = () => {
  const { pageSlug } = useParams();
  const [page, setPage] = useState(null);
  const [seoExcerpt, setSeoExcerpt] = useState('');
  const [loading, setLoading] = useState(true);
  const [updatedContentHtml, setUpdatedContentHtml] = useState('');
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState(null);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const langParam = queryParams.get('lang'); // Utilisation de 'lang' comme paramètre

  const isMobile = useBreakpoint(); // Utilisation du hook pour savoir si l'écran est mobile

  useEffect(() => {
    const fetchPageData = async () => {
      try {
        let response;

        if (langParam) {
          // On récupère d'abord la page standard pour avoir son ID
          const initialResponse = await api.get(`/pages/${pageSlug}`);

          if (initialResponse.data && initialResponse.data.id) {
            // Ensuite on utilise la route correcte pour obtenir la traduction
            // Note: utilisation de GET et non POST car c'est une lecture
            const pageId = initialResponse.data.id;
            try {
              response = await api.get(`/pages/${pageId}/lang/${langParam}`);
              setCurrentLanguage(langParam);
            } catch (translationError) {
              console.warn('Traduction non disponible, utilisation de la version par défaut:', translationError);
              response = initialResponse;
              // En cas d'erreur, on revient au français
              setCurrentLanguage('fr');
            }
          } else {
            response = initialResponse;
            // Par défaut, on utilise le français
            setCurrentLanguage('fr');
          }
        } else {
          // Sinon, utiliser l'API standard
          response = await api.get(`/pages/${pageSlug}`);
          // Si aucun paramètre de langue n'est spécifié, on utilise le français
          setCurrentLanguage('fr');
        }

        setPage(response.data || null);

        // Récupérer les langues disponibles pour cette page
        if (response.data && response.data.id) {
          // Si des langues disponibles sont déjà incluses dans la réponse
          if (response.data.availableLanguages) {
            console.log('Langues disponibles:', response.data.availableLanguages);
            setAvailableLanguages(response.data.availableLanguages);

            if (!langParam && response.data.language && response.data.language.isDefault) {
              setCurrentLanguage(response.data.language.code);
            }
          } else {
            // Sinon on pourrait faire une requête spécifique, mais pas implémentée côté backend
            console.log('Aucune langue disponible dans la réponse');
            setAvailableLanguages([]);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération de la page:', error);
        // En cas d'erreur, définir le français comme langue par défaut
        setCurrentLanguage('fr');
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  }, [pageSlug, langParam]);

  if (loading) return <div><Spinner /></div>;
  if (!page) return <div>Aucune page trouvée.</div>;

  const { title, createdAt, blocks = [], categories = [], thumbnail, files, banner, pictoAccess, slug, gallery } = page;

  const category = categories[0] || 'Aucune catégorie';

  // Trier les blocs par leur propriété `position`
  const sortedBlocks = [...blocks].sort((a, b) => a.position - b.position);

  const pageDescription = seoExcerpt || 'Une description de la page pour le SEO.';
  const pageImage = thumbnail && thumbnail.path ? `${process.env.REACT_APP_MEDIA_BASE_URL}${thumbnail.path}` : 'https://arras.fr/default-thumbnail.jpg';

  // Fonction pour changer de langue
  const changeLanguage = (langCode) => {
    // Créer une nouvelle instance de URLSearchParams avec les paramètres actuels
    const params = new URLSearchParams(location.search);

    // Mettre à jour ou ajouter le paramètre 'lang'
    params.set('lang', langCode);

    // Rediriger vers la même page avec le nouveau paramètre de langue
    window.location.href = `${location.pathname}?${params.toString()}`;
  };

  return (
    <>
      <Helmet>
        <title>Ville d'Arras | {title}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={`Ville d'Arras | ${title}`} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={pageImage} />
        <meta property="og:url" content={`https://arras.fr/pages/${slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Ville d'Arras" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Ville d'Arras | ${title}`} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />
        <link rel="canonical" href={`https://arras.fr/pages/${slug}`} />
      </Helmet>

      <div className={`${sharedStyles.pageWrapper} ${sharedStyles.newsPage}`}>
        {/* Bannière avec miniature */}
        {thumbnail && thumbnail.path && (
          <BannerWithSocialIcons
            imageUrl={`${process.env.REACT_APP_MEDIA_BASE_URL}${banner.path}`}
          />
        )}

        <div className={`page-content ${sharedStyles.pageContent}`}>
          <section className={`global-section ${sharedStyles.mainSection}`}>
            <div className={`global-container ${sharedStyles.headerContainer}`}>
              <div className={sharedStyles.header}>
                <div className={sharedStyles.headingBlock}>
                  <h1>{title}</h1>
                  <Breadcrumbs pageType="isPage" element={page} category={category} />
                </div>

                <div className={sharedStyles.metaContainer}>
                  {/* Sélecteur de langue */}
                  {availableLanguages.length > 1 && (
                    <div className={sharedStyles.meta} style={{ marginRight: '10px' }}>
                      <FontAwesomeIcon
                        icon={faLanguage}
                        className={sharedStyles.metaIcon}
                      />
                      <p className={sharedStyles.metaInfo}>Langue :</p>
                      <div className={styles.languageSelector}>
                        <select
                          value={currentLanguage || ''}
                          onChange={(e) => changeLanguage(e.target.value)}
                          aria-label="Sélectionner une langue"
                        >
                          {availableLanguages.map(lang => (
                            <option key={lang.code} value={lang.code}>
                              {lang.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  <div className={sharedStyles.meta}>
                    <ShareSocialOutline
                      color="currentColor"
                      height=""
                      width=""
                      className={sharedStyles.metaIcon}
                    />
                    <p className={sharedStyles.metaInfo}>Partager :</p>
                    <ShareButtons />
                  </div>
                </div>
              </div>

              <div className={styles.categoryContainer}>
                <ul className={`list--none ${sharedStyles.badgeList}`}>
                  {categories.map((category) => (
                    <li key={category.id}>
                      <Badge
                        label={category.name}
                        variant={category.slug}
                        link={`/categorie/${category.slug}`}
                      />
                    </li>
                  ))}
                </ul>

                {/* Indicateur de langue disponible - Version simplifiée: français/anglais uniquement */}
                <div className={styles.languageIndicator}>
                  {/* Si on est en français, afficher uniquement le bouton anglais si la traduction existe */}
                  {currentLanguage === 'fr' &&
                    availableLanguages.some(lang => lang.code === 'en') && (
                      <button
                        className={styles.flagButton}
                        onClick={() => changeLanguage('en')}
                        title="View in English"
                        aria-label="View in English"
                      >
                        <img src={britishFlag} alt="English Flag" className={styles.flag} />
                      </button>
                    )}

                  {/* Si on est en anglais, afficher uniquement le bouton français */}
                  {currentLanguage === 'en' && (
                    <button
                      className={styles.flagButton}
                      onClick={() => changeLanguage('fr')}
                      title="Voir en français"
                      aria-label="Voir en français"
                    >
                      <img src={frenchFlag} alt="Drapeau français" className={styles.flag} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className={`global-container ${sharedStyles.main}`}>
              {/* Colonne principale */}
              <div className={sharedStyles.mainColumn}>

                {isMobile && (
                  <div className={sharedStyles.mobileSummary}>
                    <Summary
                      contentHtml={blocks.find((block) => block.type === 'text')?.content || ''}
                      setUpdatedContentHtml={setUpdatedContentHtml}
                      setExcerpt={setSeoExcerpt}
                    />
                  </div>
                )}

                <div className={sharedStyles.content}>

                  {/* Affichage des blocs triés */}
                  {sortedBlocks.map((block) => {
                    if (block.type === 'text') {
                      const renderTextWithOembed = (content) => {
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(content, 'text/html');

                        const extractYoutubeVideoId = (originalUrl) => {
                          const url = new URL(originalUrl);
                          const host = url.hostname.replace(/^www\./, '').toLowerCase();
                          let videoId = null;

                          if (host === 'youtu.be') {
                            videoId = url.pathname.split('/').filter(Boolean)[0] || null;
                          } else if (
                            host === 'youtube.com' ||
                            host === 'm.youtube.com' ||
                            host === 'music.youtube.com' ||
                            host === 'youtube-nocookie.com'
                          ) {
                            if (url.pathname === '/watch') {
                              videoId = url.searchParams.get('v');
                            } else {
                              const [kind, id] = url.pathname.split('/').filter(Boolean);
                              if (['embed', 'shorts', 'live', 'v'].includes(kind)) {
                                videoId = id || null;
                              }
                            }
                          }

                          if (!videoId || !/^[\w-]{6,}$/.test(videoId)) {
                            return null;
                          }

                          return videoId;
                        };

                        // CKEditor enregistre la vidéo en <oembed url="...">, y compris les liens courts youtu.be.
                        doc.querySelectorAll('oembed').forEach((oembed) => {
                          const originalUrl = oembed.getAttribute('url');
                          if (!originalUrl) return;

                          try {
                            const videoId = extractYoutubeVideoId(originalUrl);
                            if (!videoId) return;

                            const embedUrl = `https://www.youtube.com/embed/${videoId}?rel=0`;
                            const iframe = document.createElement('iframe');
                            iframe.src = embedUrl;
                            iframe.width = '560';
                            iframe.height = '315';
                            iframe.title = 'Vidéo YouTube';
                            iframe.allow =
                              'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
                            iframe.allowFullscreen = true;
                            iframe.setAttribute('frameborder', '0');
                            iframe.setAttribute('allowtransparency', 'true');
                            oembed.parentNode.replaceChild(iframe, oembed);
                          } catch (err) {
                            console.error('URL invalide ou problème parsing:', err);
                          }
                        });

                        return doc.body.innerHTML;
                      };

                      return (
                        <div
                          key={block.id}
                          className={sharedStyles.description}
                          dangerouslySetInnerHTML={{
                            __html: renderTextWithOembed(
                              updatedContentHtml || block.content
                            ),
                          }}
                        />
                      );
                    }

                    if (block.type === 'file' && block.fileType === 'image') {
                      return (
                        <figure key={block.id} className={sharedStyles.pictureItem}>
                          <img
                            src={`${process.env.REACT_APP_MEDIA_BASE_URL}${block.path}`}
                            alt={block.description || 'Image associée'}
                            className={sharedStyles.picture}
                          />
                          {block.description && (
                            <p className={sharedStyles.credit}>
                              Crédit : {block.description}
                            </p>
                          )}
                        </figure>
                      );
                    }

                    if (block.type === 'file' && block.fileType !== 'image') {
                      const getFileIcon = (type) => {
                        switch (type) {
                          case 'pdf':
                            return faFilePdf;
                          case 'image':
                            return faFileImage;
                          default:
                            return faFileAlt;
                        }
                      };
                      return (
                        <ul key={block.id} className={`list--none ${styles.fileList}`}>
                          <li className={filestyle.fileItem}>
                            <div className={filestyle.fileInfo}>
                              <p className={filestyle.fileName}>{block.sectionTitle}</p>
                              <p className={filestyle.fileDescription}>
                                <em>
                                  {block.description || 'Aucune description disponible'}
                                </em>
                              </p>
                            </div>
                            {/* Partie droite : Icône de type de fichier et bouton d'ouverture */}
                            <div className={filestyle.fileActions}>
                              <FontAwesomeIcon
                                icon={getFileIcon(block.fileType)}
                                className={filestyle.fileIcon}
                              />
                              <button
                                onClick={() =>
                                  window.open(
                                    `${process.env.REACT_APP_MEDIA_BASE_URL}${block.path}`,
                                    '_blank'
                                  )
                                }
                                aria-label={`Ouvrir ${block.fileName} dans un nouvel onglet`}
                                title={`Ouvrir ${block.fileName} dans un nouvel onglet`}
                                className={filestyle.icon}
                              >
                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                              </button>
                            </div>
                          </li>
                        </ul>
                      );
                    }

                    if (block.type === 'picture') {
                      return (
                        <div key={block.id} className={sharedStyles.pictureItem}>
                          <img
                            src={`${process.env.REACT_APP_MEDIA_BASE_URL}${block.path}`}
                            alt={block.description || 'Image associée'}
                            className={sharedStyles.picture}
                          />
                          {block.credit && (
                            <p className={sharedStyles.credit}>Crédit : {block.credit}</p>
                          )}
                        </div>
                      );
                    }

                    // Nouveau cas pour le bloc 'card'
                    if (block.type === 'card') {
                      return <CardBlock key={block.id} block={block} />;
                    }

                    return null;
                  })}

                  {/* Liste de fichiers associés */}
                  {files && files.length > 0 && <FileList files={files} />}

                  {/* Galerie d'images associée */}
                  {gallery && (
                    <div className={sharedStyles.gallerySection}>
                      <GalleryDisplay gallery={gallery} />
                    </div>
                  )}

                </div>

                <div className={sharedStyles.actions}>
                  <Button variant="primary" onClick={() => window.print()}>
                    Imprimer
                  </Button>
                </div>
              </div>

              {/* Colonne latérale droite */}
              <div className={sharedStyles.sidebar}>
                {pictoAccess && <PictoAccess iframeHtml={pictoAccess} width="250" height="120" />}

                {!isMobile && (
                  <div className={sharedStyles.sticky}>
                    <Summary
                      contentHtml={blocks.find((block) => block.type === 'text')?.content || ''}
                      setUpdatedContentHtml={setUpdatedContentHtml}
                      setExcerpt={setSeoExcerpt}
                    />
                  </div>
                )}

              </div>
            </div>
          </section>

          {/* CTA Map */}
          <MapCta />

          {/* Liens Pratiques */}
          <LiensPratiques />
        </div>
      </div>
    </>
  );
};

export default Page;
